import React, { useMemo, useRef, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import useFetchArrivals from "../hooks/useFetchArrivals";
import { getPageConfig, DEFAULT_CONFIG, BUS_API_KEY } from "../lib/config";

function ArrivalBoard() {
  const { pageId } = useParams();
  const location = useLocation();
  const previewConfig = location.state?.previewConfig || null;
  const [config, setConfig] = useState(previewConfig);
  const [configLoading, setConfigLoading] = useState(!previewConfig);
  const [configError, setConfigError] = useState(null);

  // Load config from Firestore (skip if preview mode)
  useEffect(() => {
    if (previewConfig || !pageId) return;
    setConfigLoading(true);
    setConfigError(null);
    getPageConfig(pageId)
      .then((data) => {
        if (!data) {
          setConfigError("Page not found");
        } else {
          setConfig({
            subwayStopId: data.subwayStopId || DEFAULT_CONFIG.subwayStopId,
            subwayRoutes: data.subwayRoutes || DEFAULT_CONFIG.subwayRoutes,
            busConfigs: data.busConfigs || DEFAULT_CONFIG.busConfigs,
            transfers: data.transfers || DEFAULT_CONFIG.transfers,
          });
        }
      })
      .catch((err) => setConfigError(err.message))
      .finally(() => setConfigLoading(false));
  }, [pageId, previewConfig]);

  if (configLoading) {
    return (
      <div className="container">
        <div style={{ textAlign: "center", padding: "4rem", fontSize: "1.2rem", color: "#888" }}>
          Loading...
        </div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="container">
        <div style={{ textAlign: "center", padding: "4rem" }}>
          <h2 style={{ color: "#e74c3c" }}>{configError}</h2>
          <p style={{ color: "#888" }}>The page <strong>/{pageId}</strong> does not exist.</p>
        </div>
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="container">
      {previewConfig && (
        <div style={{
          background: '#fef3cd', color: '#856404', padding: '8px 16px',
          borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: 600
        }}>
          <span>👁️ Preview Mode — showing unsaved changes</span>
          <button onClick={() => window.history.back()}
            style={{ background: '#856404', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>
            ← Back to Settings
          </button>
        </div>
      )}
      <ArrivalBoardInner config={config} />
    </div>
  );
}

// Inner component that uses hooks (hooks can't be conditional)
function ArrivalBoardInner({ config }) {
  const systemId = "us-ny-subway";

  // Fetch primary stop arrivals
  const { data, loading, error } = useFetchArrivals(systemId, config.subwayStopId, 10000);

  // Filter by configured routes and directions
  const arrivals = useMemo(() => {
    if (!data?.stopTimes) return [];
    return data.stopTimes.filter((a) =>
      config.subwayRoutes.some(
        (r) => a.trip.route.id === r.routeId && a.trip.directionId === r.directionId
      )
    );
  }, [data, config.subwayRoutes]);

  // Collect unique transfer stop IDs from config
  const transferStopIds = useMemo(() => {
    const ids = new Set();
    (config.transfers || []).forEach((t) => {
      if (t.transferStopId) ids.add(t.transferStopId);
    });
    return Array.from(ids);
  }, [config.transfers]);

  // Fetch all transfer stop data
  const transferFetches = {};
  transferStopIds.forEach((stopId) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    transferFetches[stopId] = useFetchArrivals(systemId, stopId, 10000);
  });

  // Compute transfer waits for each arrival
  const transferWaits = useMemo(() => {
    const waits = {};
    arrivals.forEach((arrival) => {
      const tripWaits = {};

      // Find when this trip arrives at ANY transfer stop (station complex)
      let transferArrivalTime = null;
      (config.transfers || []).forEach((group) => {
        const fetchData = transferFetches[group.transferStopId]?.data;
        if (!fetchData?.stopTimes) return;
        const match = fetchData.stopTimes.find(
          (s) => s.trip.id === arrival.trip.id && s.trip.route.id === arrival.trip.route.id
        );
        if (match && transferArrivalTime === null) {
          transferArrivalTime = parseInt(match.arrival.time);
        }
      });

      if (transferArrivalTime === null) return; // trip doesn't reach any transfer stop

      // Now find next departure for each configured route at their respective stops
      (config.transfers || []).forEach((group) => {
        const fetchData = transferFetches[group.transferStopId]?.data;
        if (!fetchData?.stopTimes) return;

        group.routes.forEach((route) => {
          const candidates = fetchData.stopTimes.filter(
            (a) => a.trip.route.id === route.routeId && a.trip.directionId === route.directionId
          );
          const next = candidates.find(
            (a) => parseInt(a.arrival.time) >= transferArrivalTime
          );
          if (next) {
            tripWaits[`${route.routeId}-${route.label}`] = {
              routeId: route.routeId,
              label: route.label,
              wait: Math.round((parseInt(next.arrival.time) - transferArrivalTime) / 60),
              color: next.trip.route.color,
            };
          }
        });
      });
      waits[arrival.trip.id] = tripWaits;
    });
    return waits;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivals, config.transfers, ...Object.values(transferFetches).map(f => f.data)]);

  // ---- BUS ARRIVALS ----
  const [busArrivals, setBusArrivals] = useState([]);
  const [busError, setBusError] = useState(null);

  useEffect(() => {
    const stops = (config.busConfigs || []).filter(c => c.busStopId);
    if (stops.length === 0) return;
    let cancelled = false;
    function fetchAllBus() {
      setBusError(null);
      const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const fetches = stops.map(bc => {
        const stopCode = bc.busStopId.replace(/^MTA_/, "");
        let fetchUrl;
        if (isLocal) {
          fetchUrl = `/busapi/siri/stop-monitoring.json?key=${BUS_API_KEY}&MonitoringRef=${stopCode}`;
        } else {
          const busApiUrl = `https://bustime.mta.info/api/siri/stop-monitoring.json?key=${BUS_API_KEY}&MonitoringRef=${stopCode}`;
          fetchUrl = `/api/proxy?url=${encodeURIComponent(busApiUrl)}`;
        }
        return fetch(fetchUrl, { headers: { Accept: "application/json" } })
          .then(res => res.json())
          .then(data => {
            const visits = data?.Siri?.ServiceDelivery?.StopMonitoringDelivery?.[0]?.MonitoredStopVisit || [];
            // Only keep visits matching the configured route
            return visits
              .filter(v => {
                const lineRef = v.MonitoredVehicleJourney?.LineRef || "";
                return lineRef === bc.busRouteId;
              })
              .map(v => ({ ...v, _busLabel: bc.label || "" }));
          })
          .catch(() => []);
      });
      Promise.all(fetches).then(results => {
        if (!cancelled) setBusArrivals(results.flat());
      });
    }
    fetchAllBus();
    const interval = setInterval(fetchAllBus, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [config.busConfigs]);

  // Auto-refresh tick
  const [, setRefreshTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setRefreshTick((t) => t + 1), 10000);
    return () => clearInterval(interval);
  }, []);

  const arrivalListRef = useRef(null);
  const scrollPosRef = useRef(0);

  useEffect(() => {
    if (loading && arrivalListRef.current) {
      scrollPosRef.current = arrivalListRef.current.scrollTop;
    }
  }, [loading]);

  useEffect(() => {
    if (!loading && arrivalListRef.current) {
      arrivalListRef.current.scrollTop = scrollPosRef.current;
    }
  }, [loading, arrivals.length]);

  function renderRouteBadge(route, colorOverride, small = false) {
    const color = colorOverride || (route?.color ? `#${route.color}` : "#333");
    return (
      <span
        className="route-badge"
        style={{
          background: color,
          minWidth: small ? 44 : 90,
          height: small ? 44 : 90,
          lineHeight: small ? "44px" : "90px",
          fontSize: small ? "2rem" : "3.2rem",
          marginRight: small ? 8 : 40,
          boxShadow: small ? "none" : undefined,
        }}
      >
        {route?.id || route?.name || "?"}
      </span>
    );
  }

  // Unified, time-sorted arrivals
  const now = Date.now();
  const isWeekday = (() => {
    const day = new Date().getDay();
    return day >= 1 && day <= 5;
  })();

  const subwayArrivals = arrivals.map((arrival) => {
    const secondsToLeave = parseInt(arrival.departure.time) - Math.floor(now / 1000);
    return {
      type: "subway",
      key: `subway-${arrival.trip.route.id}-${arrival.departure.time}`,
      route: arrival.trip.route,
      time: secondsToLeave,
      waits: transferWaits[arrival.trip.id] || {},
    };
  });

  const busArrivalsUnified = busArrivals
    .filter((visit) => {
      const expected = visit.MonitoredVehicleJourney?.MonitoredCall?.ExpectedArrivalTime;
      return !!expected;
    })
    .map((visit) => {
      const mvj = visit.MonitoredVehicleJourney;
      let route = mvj?.LineRef || "?";
      if (route.startsWith("MTA NYCT_")) route = route.replace("MTA NYCT_", "");
      if (route.startsWith("MTABC_")) route = route.replace("MTABC_", "");
      const dest = visit._busLabel || mvj?.DestinationName || "";
      const expected = mvj?.MonitoredCall?.ExpectedArrivalTime;
      let min = null;
      let time = null;
      if (expected) {
        time = Math.floor((new Date(expected).getTime() - now) / 1000);
        min = time > 60 ? `${Math.floor(time / 60)} min` : time > 0 ? `${time} sec` : "Due";
      }
      const vehicleRef = mvj?.VehicleRef || "";
      return {
        type: "bus",
        key: `bus-${route}-${expected}-${vehicleRef}`,
        route: { id: route },
        time,
        min,
        dest,
        routeRaw: route,
        expected,
        vehicleRef,
      };
    })
    .filter(
      (item, idx, arr) =>
        arr.findIndex(
          (x) =>
            x.routeRaw === item.routeRaw &&
            x.expected === item.expected &&
            x.dest === item.dest &&
            x.vehicleRef === item.vehicleRef
        ) === idx
    );

  let unifiedArrivals;
  if (isWeekday) {
    const sortedSubway = subwayArrivals.filter((item) => item.time !== null && item.time >= 0).sort((a, b) => a.time - b.time);
    const sortedBus = busArrivalsUnified.filter((item) => item.time !== null && item.time >= 0).sort((a, b) => a.time - b.time);
    const firstTwoSubway = sortedSubway.slice(0, 2);
    const rest = [...sortedSubway.slice(2), ...sortedBus].sort((a, b) => a.time - b.time);
    unifiedArrivals = [...firstTwoSubway, ...rest];
  } else {
    unifiedArrivals = [...subwayArrivals, ...busArrivalsUnified]
      .filter((item) => item.time !== null && item.time >= 0)
      .sort((a, b) => a.time - b.time);
  }

  return (
    <div>
      {error && <p style={{ color: "red", textAlign: "center" }}>Error: {error}</p>}
      {!loading && arrivals.length === 0 && busArrivals.length === 0 && (
        <p style={{ textAlign: "center", color: "#888" }}>No upcoming arrivals.</p>
      )}
      <ul className="arrival-list" ref={arrivalListRef}>
        {unifiedArrivals.map((item) => {
          if (item.type === "subway") {
            const { route, time, waits } = item;
            const waitEntries = Object.values(waits);
            return (
              <li key={item.key} className="arrival-item">
                {renderRouteBadge(route)}
                <div className="arrival-details">
                  <div className="arrival-time">
                    {time > 60 ? `${Math.floor(time / 60)} min` : `${time > 0 ? time : 0} sec`}
                  </div>
                  {waitEntries.length > 0 && (
                    <div className="transfer-badges-row" style={{ marginTop: 4 }}>
                      {waitEntries.map((w) => (
                        <span key={`${w.routeId}-${w.label}`} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          {renderRouteBadge({ id: w.routeId }, w.color ? `#${w.color}` : undefined, true)}
                          <span style={{ fontSize: "1.2rem", color: "#888", margin: "0 2px", fontWeight: 500 }}>
                            {w.label}
                          </span>
                          <span style={{ minWidth: 20, fontSize: "1.2rem", fontWeight: 600, color: "#1a7f37" }}>
                            {w.wait}m
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          } else if (item.type === "bus") {
            const { route, min, dest, key } = item;
            let displayRoute = { ...route };
            if (displayRoute.id && displayRoute.id.startsWith("Q")) {
              displayRoute = { ...displayRoute, id: displayRoute.id.slice(1) };
            }
            return (
              <li key={key} className="arrival-item">
                {renderRouteBadge(displayRoute, "#0055a4")}
                <div className="arrival-details">
                  <div className="arrival-time">{min || "-"}</div>
                  {dest && (
                    <div className="transfer-badges-row" style={{ marginTop: 4 }}>
                      <span style={{ fontSize: "1.2rem", color: "#888", margin: "0 2px", fontWeight: 500 }}>
                        {dest}
                      </span>
                    </div>
                  )}
                </div>
              </li>
            );
          }
          return null;
        })}
        {busError && (
          <li className="arrival-item" style={{ color: "red" }}>Bus error: {busError}</li>
        )}
      </ul>
    </div>
  );
}

export default ArrivalBoard;